import { View } from "react-native";
import Svg, { Ellipse } from "react-native-svg";

import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { colors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import type { InjectionZones as Zones } from "../../pose/zones";

const ns = "care.injection_coach";

/**
 * The injection area, outlined on her own body and said in words.
 *
 * This is what the injection step shows over the camera, in place of a drawn
 * abdomen. A diagram of somebody's body laid over hers has to choose a shape,
 * and whichever it chooses is wrong for most people. An outline drawn from her
 * own landmarks does not choose: the shape is hers, at her size, wherever she
 * is standing.
 *
 * ### One outline, because one is all that can be honest
 *
 * There were two. The second marked the navel, and the navel is not detected —
 * the model has no landmark for it, so it was drawn at a fraction of the torso
 * and, on most bodies, nowhere near one. Pointing at a named landmark and
 * missing is a different kind of wrong from indicating a region: the region
 * tolerates the error it was always going to have. She can see her own navel,
 * and the step's words already say to stay clear of it.
 *
 * So the overlay shows the one thing she cannot judge by looking — roughly
 * where on her the step is pointing — and is labelled in her own language. The
 * label is real text rather than anything drawn: the SVG below is stretched to
 * the box (`preserveAspectRatio` is `none`, which is what makes the per-axis
 * fractions land on the axes they were measured against) and lettering inside
 * it would stretch with it.
 *
 * The label names the outline rather than instructing her. It read "Inject in
 * this area", an imperative attached to a position this file's own header calls
 * a presentational estimate — the same defect the navel outline was deleted
 * for. The instruction is the sentence above the picture, which was clinically
 * reviewed; this only says what the shape is. No distance is written, for the
 * same reason no figure carries a number. The key kept the imperative for a
 * while after the words lost it, which is its own small version of the same
 * thing: a name that says more than the copy does.
 */
export function InjectionZones({ zones }: { zones: Zones }) {
  const t = useTranslations();

  return (
    <View
      pointerEvents="none"
      style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0 }}
    >
      <Svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <Ellipse
          cx={zones.area.x * 100}
          cy={zones.area.y * 100}
          rx={zones.areaRadius.rx * 100}
          ry={zones.areaRadius.ry * 100}
          fill="none"
          stroke={colors.biologyAmber}
          strokeWidth="1.2"
          strokeDasharray="3 2.4"
          strokeLinecap="round"
        />
      </Svg>

      <ZoneLabel
        x={zones.area.x}
        y={zones.area.y + zones.areaRadius.ry}
        text={t(`${ns}.injection_area`)}
        tone={colors.biologyAmber}
      />
    </View>
  );
}

/**
 * One label, pinned to a fraction of the preview.
 *
 * Centred on its point with a translate rather than positioned from its corner,
 * so it stays over the outline it names whatever its own width turns out to be.
 * Kept off the very edges: a label half under the border reads as a rendering
 * fault, and the whole point of these is to be read.
 */
function ZoneLabel({
  x,
  y,
  text,
  tone,
}: {
  x: number;
  y: number;
  text: string;
  tone: string;
}) {
  const clamped = Math.min(0.92, Math.max(0.08, y));

  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        left: `${Math.min(0.85, Math.max(0.15, x)) * 100}%`,
        top: `${clamped * 100}%`,
        transform: [{ translateX: -60 }, { translateY: 6 }],
        width: 120,
        alignItems: "center",
      }}
    >
      <View
        style={{
          paddingVertical: 3,
          paddingHorizontal: 8,
          borderRadius: 999,
          backgroundColor: "rgba(28,18,14,0.66)",
        }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{ fontSize: 10.5, fontWeight: "700", color: tone }}
        >
          {text}
        </Text>
      </View>
    </View>
  );
}
